// src/components/app-shell/InactivityLogout.tsx
import { useEffect, useRef, useState } from "react";
import { Clock, MousePointer } from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import {
  ACTIVITY_EVENTS,
  IDLE_WARN_BEFORE_MS,
  INACTIVITY_LIMIT_MS,
  JWT_WARN_BEFORE_MS,
} from "#/lib/session-policy";

// All of the timings used below (idle window, warning lead times) live in
// lib/session-policy.ts, configurable via the frontend's VITE_SESSION_*
// env vars -- shared with AuthProvider's token-refresh scheduling so the
// idle timer here and the silent JWT refresh can never disagree about what
// counts as "active."

type WarningReason = "idle" | "jwt-expiry";

export function InactivityLogout() {
  const { isAuthenticated, signOut, sessionExpiresAt } = useAuth();

  const idleTimeoutRef   = useRef<number | null>(null);
  const idleWarnRef      = useRef<number | null>(null);
  const jwtWarnRef       = useRef<number | null>(null);
  const jwtTimeoutRef    = useRef<number | null>(null);
  const countdownRef     = useRef<number | null>(null);

  const [showWarning, setShowWarning]   = useState(false);
  const [warningReason, setWarningReason] = useState<WarningReason>("idle");
  const [secondsLeft, setSecondsLeft]   = useState(0);

  // ── JWT expiry timer — set once when sessionExpiresAt changes ────────────
  useEffect(() => {
    if (!isAuthenticated || !sessionExpiresAt) return;

    const expiresMs = new Date(sessionExpiresAt).getTime();
    const nowMs = Date.now();
    const msUntilExpiry = expiresMs - nowMs;

    if (msUntilExpiry <= 0) {
      // Already expired (stale storage) — sign out immediately.
      void signOut();
      return;
    }

    const warnAt = msUntilExpiry - JWT_WARN_BEFORE_MS;

    if (warnAt > 0) {
      jwtWarnRef.current = window.setTimeout(() => {
        const remaining = Math.max(
          Math.round((new Date(sessionExpiresAt).getTime() - Date.now()) / 1000),
          0,
        );
        setWarningReason("jwt-expiry");
        setShowWarning(true);
        setSecondsLeft(remaining);
        countdownRef.current = window.setInterval(() => {
          setSecondsLeft((s) => Math.max(s - 1, 0));
        }, 1_000);
      }, warnAt);
    } else {
      // Less than JWT_WARN_BEFORE_MS left — show warning immediately.
      const remaining = Math.max(Math.round(msUntilExpiry / 1000), 0);
      setWarningReason("jwt-expiry");
      setShowWarning(true);
      setSecondsLeft(remaining);
      countdownRef.current = window.setInterval(() => {
        setSecondsLeft((s) => Math.max(s - 1, 0));
      }, 1_000);
    }

    jwtTimeoutRef.current = window.setTimeout(() => {
      void signOut();
    }, msUntilExpiry);

    return () => {
      if (jwtWarnRef.current)    window.clearTimeout(jwtWarnRef.current);
      if (jwtTimeoutRef.current) window.clearTimeout(jwtTimeoutRef.current);
      if (countdownRef.current)  window.clearInterval(countdownRef.current);
    };
  }, [isAuthenticated, sessionExpiresAt, signOut]);

  // ── Idle activity timer — resets on every user interaction ───────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    function clearIdleTimers() {
      if (idleTimeoutRef.current) window.clearTimeout(idleTimeoutRef.current);
      if (idleWarnRef.current)    window.clearTimeout(idleWarnRef.current);
    }

    function resetIdleTimers() {
      clearIdleTimers();
      // Don't dismiss a JWT-expiry warning on mouse movement.
      setShowWarning((prev) => {
        if (prev && warningReason === "idle") return false;
        return prev;
      });

      idleWarnRef.current = window.setTimeout(() => {
        setWarningReason("idle");
        setShowWarning(true);
        setSecondsLeft(Math.round(IDLE_WARN_BEFORE_MS / 1000));
        if (countdownRef.current) window.clearInterval(countdownRef.current);
        countdownRef.current = window.setInterval(() => {
          setSecondsLeft((s) => Math.max(s - 1, 0));
        }, 1_000);
      }, INACTIVITY_LIMIT_MS - IDLE_WARN_BEFORE_MS);

      idleTimeoutRef.current = window.setTimeout(() => {
        void signOut();
      }, INACTIVITY_LIMIT_MS);
    }

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, resetIdleTimers, { passive: true }));
    resetIdleTimers();

    return () => {
      clearIdleTimers();
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, resetIdleTimers));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, signOut]);

  if (!showWarning) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const countdown = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  const isJwtExpiry = warningReason === "jwt-expiry";

  return (
    <div className="fixed bottom-5 right-5 z-[999] w-80 rounded-2xl border border-amber-200 bg-white p-4 shadow-xl">
      <div className="mb-2 flex items-center gap-2">
        {isJwtExpiry ? (
          <Clock className="h-4 w-4 shrink-0 text-amber-600" />
        ) : (
          <MousePointer className="h-4 w-4 shrink-0 text-amber-600" />
        )}
        <p className="font-semibold text-slate-900">
          {isJwtExpiry ? "Session expiring" : "Inactive session"}
        </p>
      </div>

      <p className="text-sm text-slate-600">
        {isJwtExpiry ? (
          <>
            Your session expires in <strong>{countdown}</strong>. Sign in again to continue.
          </>
        ) : (
          <>
            You'll be signed out in <strong>{countdown}</strong> due to inactivity.
            Move your mouse or press any key to stay signed in.
          </>
        )}
      </p>

      {!isJwtExpiry && (
        <button
          onClick={() => {
            setShowWarning(false);
            document.dispatchEvent(new MouseEvent("mousemove"));
          }}
          className="mt-3 w-full rounded-xl bg-emerald-600 py-2 text-sm font-bold text-white hover:bg-emerald-700"
        >
          Stay signed in
        </button>
      )}

      {isJwtExpiry && (
        <button
          onClick={() => void signOut()}
          className="mt-3 w-full rounded-xl border border-slate-200 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Sign out now
        </button>
      )}
    </div>
  );
}
